/**
 * Location Storage Helper Functions
 * Handles caching location data in localStorage with expiration logic
 */

const LOCATION_STORAGE_KEY = 'user_cached_location';
const MANUAL_LOCATION_KEY = 'user_manual_location';
const SESSION_ID_KEY = 'location_session_id';
const CACHE_EXPIRATION_DAYS = 100;
const CACHE_EXPIRATION_MS = CACHE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

/**
 * Get cached location from localStorage
 * Returns null if no cache exists or cache is expired
 * @returns {Object|null} - { latitude, longitude, timestamp } or null
 */
export const getCachedLocation = () => {
  try {
    const cachedData = localStorage.getItem(LOCATION_STORAGE_KEY);
    
    if (!cachedData) {
      return null;
    }

    const locationData = JSON.parse(cachedData);
    
    // Check if cache is expired
    const now = Date.now();
    const cacheAge = now - locationData.timestamp;
    
    if (cacheAge > CACHE_EXPIRATION_MS) {
      // Cache expired, remove it
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      return null;
    }

    return locationData;
  } catch (error) {
    console.error('Error reading cached location:', error);
    return null;
  }
};

/**
 * Save location to localStorage
 * @param {number} latitude 
 * @param {number} longitude 
 * @returns {Object} - Saved location data
 */
export const setCachedLocation = (latitude, longitude) => {
  try {
    const locationData = {
      latitude,
      longitude,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(locationData));
    return locationData;
  } catch (error) {
    console.error('Error saving location to cache:', error);
    return null;
  }
};

/**
 * Check if cached location exists and is still valid
 * @returns {boolean}
 */
export const hasValidCache = () => {
  const cached = getCachedLocation();
  return cached !== null;
};

/**
 * Clear cached location from localStorage
 */
export const clearCachedLocation = () => {
  try {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing cached location:', error);
  }
};

/**
 * Calculate distance between two coordinates in kilometers
 * Uses Haversine formula
 * @param {number} lat1 
 * @param {number} lon1 
 * @param {number} lat2 
 * @param {number} lon2 
 * @returns {number} - Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Set manual location (user selected manually)
 * When set, auto-update will be skipped
 * @param {number} latitude 
 * @param {number} longitude 
 */
export const setManualLocation = (latitude, longitude) => {
  try {
    const manualData = {
      latitude,
      longitude,
      timestamp: Date.now(),
    };
    localStorage.setItem(MANUAL_LOCATION_KEY, JSON.stringify(manualData));
    return manualData;
  } catch (error) {
    console.error('Error saving manual location:', error);
    return null;
  }
};

/**
 * Get manual location if set by user
 * @returns {Object|null} - { latitude, longitude, timestamp } or null
 */
export const getManualLocation = () => {
  try {
    const data = localStorage.getItem(MANUAL_LOCATION_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Error reading manual location:', error);
    return null;
  }
};

/**
 * Clear manual location (re-enable auto-detection)
 */
export const clearManualLocation = () => {
  try {
    localStorage.removeItem(MANUAL_LOCATION_KEY);
  } catch (error) {
    console.error('Error clearing manual location:', error);
  }
};

/**
 * Check if user has manually set a location
 * @returns {boolean}
 */
export const hasManualLocation = () => {
  return getManualLocation() !== null;
};

/**
 * Generate and store a new session ID (call on app mount)
 * @returns {string} - The new session ID
 */
export const createLocationSession = () => {
  try {
    const sessionId = Date.now().toString();
    localStorage.setItem(SESSION_ID_KEY, sessionId);
    return sessionId;
  } catch (error) {
    console.error('Error creating location session:', error);
    return null;
  }
};

/**
 * Get the current session ID
 * @returns {string|null}
 */
export const getLocationSession = () => {
  try {
    return localStorage.getItem(SESSION_ID_KEY);
  } catch (error) {
    return null;
  }
};

/**
 * Clear the session ID (call when user manually sets location)
 */
export const clearLocationSession = () => {
  try {
    localStorage.removeItem(SESSION_ID_KEY);
  } catch (error) {
    console.error('Error clearing location session:', error);
  }
};
